import { BackgroundUpload } from "@/components/avatar-upload/BackgroundUpload.tsx";
import { ImageUploadModal } from "@/components/avatar-upload/ImageUploadModal.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { CurrencySelect } from "@/components/select/CurrencySelect.tsx";
import { useRecent } from "@/hooks/use-recent.hook.ts";
import { ApiRecentKey } from "@/types/recents/recents.types.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import {
  useOrganizationDeleteBackground,
  useOrganizationDeleteLogo,
  useOrganizationSetBackground,
  useOrganizationSetLogo,
  useOrganizationSettings,
  useOrganizationUpdateSettings,
} from "@/queries/settings/use-organization.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import getTimezones from "@/services/timezone-service.ts";
import { useThemeStore } from "@/stores/theme.store.ts";
import { AvailableThemes } from "@/themes/theme.types.ts";
import { ApiFile } from "@/types/common.types.ts";
import {
  BirthdayDisplayType,
  EmployeeFullNameRenderType,
} from "@/types/employees/employee.types.ts";
import { ApiFilePathTypes } from "@/types/file.types.ts";
import {
  ApiOrganizationBackgroundApplyTo,
  ApiOrganizationPayload,
  BirthdayDisplayOptions,
  DateFormatOptions,
  EmployeeDatesFormat,
  LanguageOptions,
  Languages,
  NameFormatOptions,
} from "@/types/organization.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Avatar,
  Box,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Input,
  Page,
  Select,
  Stack,
  Switch,
  Title,
  useModals,
  useToasts,
} from "@skailer-com/uikit";
import { useEffect, useMemo } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";
import { z } from "zod";

export default function SettingsOrganization() {
  const { t } = useTranslation();
  const { showInfo } = useToasts();
  const { data: employee } = useEmployeeMe();
  const modalsService = useModals();
  const organizationBackgroundSet = useOrganizationSetBackground();
  const organizationBackgroundDelete = useOrganizationDeleteBackground();
  const { theme } = useThemeStore();

  useTitle(t("core:organization_settings"), t("common:settings"));

  const requiredMessage = t("errors:field_required");
  const SettingsSchemaObject = z
    .object({
      title: z.string().min(1, requiredMessage),
      language: z.enum(Languages).optional(),
      timezone: z.string().optional(),
      employee_name_format: z.enum(EmployeeFullNameRenderType).optional(),
      default_date_format: z.enum(EmployeeDatesFormat).optional(),
      theme: z.string().optional(),
      subdomain: z.string().min(1, requiredMessage),
      birthday_display: z.enum(BirthdayDisplayType).optional(),
      currency: z.string().min(1, requiredMessage),
      standard_weekly_hours: z
        .number()
        .min(0, t("api_errors:minimum_value_must", { minimum: 0 }))
        .max(168, t("api_errors:maximum_value_must", { maximum: 168 }))
        .nullable()
        .optional(),
      personnel_number_auto_enabled: z.boolean().optional(),
      personnel_number_next_value: z.number().nullable().optional(),
    })
    .superRefine((data, context) => {
      if (data.personnel_number_auto_enabled) {
        if (
          data.personnel_number_next_value === null ||
          data.personnel_number_next_value === undefined
        ) {
          context.addIssue({
            code: "custom",
            path: ["personnel_number_next_value"],
            message: requiredMessage,
          });
        } else if (data.personnel_number_next_value < 1) {
          context.addIssue({
            code: "custom",
            path: ["personnel_number_next_value"],
            message: t("api_errors:minimum_value_must", { minimum: 1 }),
          });
        }
      }
    });

  const {
    reset,
    register,
    handleSubmit,
    control,
    formState: { errors, dirtyFields },
    getValues,
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(SettingsSchemaObject),
  });

  const personnelNumberAutoEnabled = useWatch({ control, name: "personnel_number_auto_enabled" });

  const { canIView } = usePermissions();
  const { data: organizationSettings } = useOrganizationSettings(
    canIView(ApiPermission.settings_organization)
  );
  const organizationUpdate = useOrganizationUpdateSettings(control);
  const organizationLogoDelete = useOrganizationDeleteLogo();
  const organizationLogoSet = useOrganizationSetLogo();
  const { addRecent } = useRecent();

  useEffect(() => {
    if (organizationSettings) {
      reset({
        ...organizationSettings,
        theme: organizationSettings.theme || theme,
      });
    }
  }, [organizationSettings, theme, reset]);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.organization.settingsViewed);
  });

  const onSubmit = handleSubmit(async (settingsData) => {
    const settingsToUpdate: ApiOrganizationPayload = {
      title: settingsData.title,
      language: settingsData.language,
      timezone: settingsData.timezone,
      employee_name_format: settingsData.employee_name_format,
      default_date_format: settingsData.default_date_format,
      theme: settingsData.theme,
      birthday_display: settingsData.birthday_display,
      currency: settingsData.currency,
      standard_weekly_hours: settingsData.standard_weekly_hours ?? null,
      personnel_number_auto_enabled: settingsData.personnel_number_auto_enabled,
      personnel_number_next_value: settingsData.personnel_number_next_value ?? null,
    };

    organizationUpdate.mutate(settingsToUpdate, {
      onSuccess: () => {
        if (dirtyFields.currency) {
          addRecent([{ type: ApiRecentKey.currency, values: [settingsData.currency] }]);
        }
        analyticsService.trackEvent(analyticEvents.organization.settingsEdited, {
          [analyticProperties.actionType]: "Details Edited",
          [analyticProperties.source]: "General",
        });
        showInfo({ title: t("common:you_changes_were_saved") });
      },
    });
  });

  const availableOptions = useMemo(() => {
    if (employee)
      return LanguageOptions.filter((option) =>
        employee.available_languages.some((language) => language == option.id)
      );
    return [];
  }, [employee]);

  const handleAvatarUpload = () => {
    if (!employee) return;
    modalsService.openModal(ImageUploadModal, {
      modalTitle: t("common:logo"),
      path: ApiFilePathTypes.organization,
      onSave: async (file: ApiFile) => {
        try {
          await organizationLogoSet.mutateAsync(file.uuid);
          analyticsService.trackEvent(analyticEvents.organization.settingsEdited, {
            [analyticProperties.actionType]: "Photo Updated",
          });
          return true;
        } catch {
          return false;
        }
      },
    });
  };

  const handleAvatarDelete = async () => {
    if (!employee) return;

    try {
      await organizationLogoDelete.mutateAsync();
      analyticsService.trackEvent(analyticEvents.organization.settingsEdited, {
        [analyticProperties.actionType]: "Photo Deleted",
      });
    } catch {
      return;
    }
  };

  const handleBackgroundUpload = () => {
    if (!employee) return;
    modalsService.openModal(ImageUploadModal, {
      modalTitle: t("common:background"),
      path: ApiFilePathTypes.organization,
      saveSelect: {
        label: t("core:background_apply_to"),
        defaultValue: ApiOrganizationBackgroundApplyTo.new_employees,
        options: [
          {
            id: ApiOrganizationBackgroundApplyTo.new_employees,
            title: t("core:background_apply_to_new_employees"),
          },
          {
            id: ApiOrganizationBackgroundApplyTo.without_own_background,
            title: t("core:background_apply_to_without_own_background"),
          },
          {
            id: ApiOrganizationBackgroundApplyTo.all_employees,
            title: t("core:background_apply_to_all_employees"),
          },
        ],
      },
      onSave: async (file: ApiFile, selectedValue?: string) => {
        const applyTo =
          Object.values(ApiOrganizationBackgroundApplyTo).find(
            (value) => value === selectedValue
          ) ?? ApiOrganizationBackgroundApplyTo.new_employees;
        try {
          await organizationBackgroundSet.mutateAsync({
            fileUUID: file.uuid,
            applyTo,
          });
          analyticsService.trackEvent(analyticEvents.organization.settingsEdited, {
            [analyticProperties.actionType]: "Background Updated",
          });
          return true;
        } catch {
          return false;
        }
      },
    });
  };

  const handleBackgroundDelete = async () => {
    if (!employee) return;
    try {
      await organizationBackgroundDelete.mutateAsync();
      analyticsService.trackEvent(analyticEvents.organization.settingsEdited, {
        [analyticProperties.actionType]: "Background Deleted",
      });
    } catch {
      return;
    }
  };

  return (
    <>
      <Page>
        <Page.Header
          title={t("core:organization_settings")}
          showBack={true}
          navigateTo={"/settings/general"}
        />
        <Page.Content>
          <Stack gap={"xl"} className={"container-sm mx-auto"}>
            <form onSubmit={onSubmit}>
              <Title
                size={"lg"}
                paddingBottom
                paddingTop
                header={t("core:company_details")}
                caption={t("core:company_details_description")}
              />

              <Input
                label={t("common:title")}
                required={true}
                type={"text"}
                {...register("title", {
                  value: "",
                })}
                error={errors.title?.message}
              />

              <Controller
                control={control}
                name="language"
                render={({ field: { value, onChange } }) => (
                  <Select
                    label={t("common:language_default")}
                    options={availableOptions}
                    emptyTitle={t("common:select")}
                    value={value}
                    onChange={onChange}
                    error={errors.language?.message}
                  />
                )}
              />

              <Controller
                control={control}
                name="timezone"
                render={({ field: { value, onChange } }) => (
                  <Select
                    label={t("common:timezone_default")}
                    options={getTimezones()}
                    emptyTitle={t("common:select")}
                    searchable
                    value={value}
                    onChange={onChange}
                    error={errors.timezone?.message}
                  />
                )}
              />

              <Controller
                control={control}
                name="employee_name_format"
                render={({ field: { value, onChange } }) => (
                  <Select
                    label={t("core:name_display_format")}
                    options={NameFormatOptions}
                    emptyTitle={t("common:select")}
                    value={value}
                    onChange={onChange}
                    error={errors.employee_name_format?.message}
                  />
                )}
              />

              <Controller
                control={control}
                name="default_date_format"
                render={({ field: { value, onChange } }) => (
                  <Select
                    label={t("common:date_format")}
                    options={DateFormatOptions}
                    emptyTitle={t("common:select")}
                    value={value}
                    onChange={onChange}
                    error={errors.default_date_format?.message}
                  />
                )}
              />
              <Controller
                control={control}
                name="theme"
                render={({ field: { value, onChange } }) => (
                  <Select
                    label={t("core:default_theme")}
                    options={AvailableThemes}
                    emptyTitle={t("common:select")}
                    value={value}
                    onChange={onChange}
                    error={errors.theme?.message}
                  />
                )}
              />

              <Controller
                control={control}
                name="birthday_display"
                render={({ field: { value, onChange } }) => (
                  <Select
                    label={t("core:birthday_display")}
                    options={BirthdayDisplayOptions}
                    emptyTitle={t("common:select")}
                    value={value}
                    onChange={onChange}
                    error={errors.birthday_display?.message}
                  />
                )}
              />
              <Controller
                control={control}
                name={`currency`}
                render={({ field: { value, onChange } }) => (
                  <CurrencySelect
                    label={t("common:currency")}
                    emptyTitle={t("common:select")}
                    value={value}
                    searchable={true}
                    onChange={onChange}
                    error={errors.currency?.message}
                  />
                )}
              />

              <Input
                label={t("core:standard_weekly_hours")}
                type={"number"}
                min={0}
                max={168}
                step={0.5}
                {...register("standard_weekly_hours", {
                  setValueAs: (value) => (value === "" ? null : Number(value)),
                })}
                error={errors.standard_weekly_hours?.message}
              />
              <Box className={"py-2 text-sm text-secondary"}>
                {t("core:standard_weekly_hours_hint")}
              </Box>

              <Box className={"py-2"}>
                <Controller
                  control={control}
                  name="personnel_number_auto_enabled"
                  render={({ field: { value, onChange } }) => (
                    <Switch
                      size={"sm"}
                      checked={value ?? false}
                      onChange={onChange}
                      title={t("core:employee_number_enabled_title")}
                      description={t("core:employee_number_enabled_description")}
                    />
                  )}
                />
                {personnelNumberAutoEnabled && (
                  <Box className={"pl-9"}>
                    <Input
                      type={"number"}
                      min={1}
                      label={t("core:employee_number_next_value_title")}
                      {...register("personnel_number_next_value", {
                        setValueAs: (value) => (value === "" ? null : Number(value)),
                      })}
                      error={errors.personnel_number_next_value?.message}
                    />
                  </Box>
                )}
              </Box>

              <Title size={"xs"} paddingBottom paddingTop header={t("common:logo")} />

              <Stack className={"bg-dark/5 p-5 rounded-xs"} justify={"center"} items={"center"}>
                <DropdownMenu
                  trigger={
                    employee && (
                      <Box className={"relative w-41 h-41 group cursor-pointer "}>
                        <Avatar
                          url={organizationSettings?.logo_url}
                          size={"2xl"}
                          acronym={getValues("title")}
                        />
                        <Box
                          className={
                            "bg-dark/20 opacity-0 group-hover:opacity-100 absolute inset-0 rounded-lg flex items-center justify-center"
                          }
                        >
                          <FontAwesomeIcon
                            icon={"fa-light fa-camera"}
                            className={"text-[40px] text-light "}
                          />
                        </Box>
                      </Box>
                    )
                  }
                >
                  <DropdownMenuItem
                    leftIcon={<FontAwesomeIcon icon={"fa-light fa-image"} />}
                    onClick={handleAvatarUpload}
                    title={t("common:upload_new")}
                  />
                  <DropdownMenuItem
                    leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
                    onClick={handleAvatarDelete}
                    variant={"menuDanger"}
                    title={t("common:delete")}
                  />
                </DropdownMenu>
              </Stack>
              <BackgroundUpload
                backgroundUrl={organizationSettings?.background_url}
                onUpload={handleBackgroundUpload}
                onDelete={handleBackgroundDelete}
                title={t("core:default_employee_background")}
              />
              <ServerErrorField errors={errors} />
            </form>

            <Button isLoading={organizationUpdate.isPending} onClick={onSubmit} size={"lg"}>
              {t("common:save")}
            </Button>
          </Stack>
        </Page.Content>
      </Page>
    </>
  );
}
